import { useMutation } from '@apollo/client';
import { WorldMapSetInput } from '__generated__/globalTypes';
import {
    cacheAddWorldMapSet,
    cacheRemoveWorldMapSet,
    cacheUpdateWorldMapSet
} from 'cache/mapSetCacheUpdateMethods';
import {
    CreateWorldMapSet,
    CreateWorldMapSetVariables
} from 'graphql/mutation/__generated__/CreateWorldMapSet';
import {
    RemoveWorldMapSet,
    RemoveWorldMapSetVariables
} from 'graphql/mutation/__generated__/RemoveWorldMapSet';
import {
    UpdateWorldMapSet,
    UpdateWorldMapSetVariables
} from 'graphql/mutation/__generated__/UpdateWorldMapSet';
import {
    CREATE_WORLD_MAP_SET,
    REMOVE_WORLD_MAP_SET,
    UPDATE_WORLD_MAP_SET
} from 'graphql/mutation/worldMutation';
import { WorldMap_worldMap_worldMapSets } from 'graphql/query/__generated__/WorldMap';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { useContext } from 'react';
import { CtxEdit } from 'session/ctxEdit';

export const useMapSetActions = () => {
    const ctxEdit = useContext(CtxEdit);
    const [createSetMutation] = useMutation<CreateWorldMapSet, CreateWorldMapSetVariables>(
        CREATE_WORLD_MAP_SET
    );

    const [updateSetMutation] = useMutation<UpdateWorldMapSet, UpdateWorldMapSetVariables>(
        UPDATE_WORLD_MAP_SET
    );
    const [removeSetMutation] = useMutation<RemoveWorldMapSet, RemoveWorldMapSetVariables>(
        REMOVE_WORLD_MAP_SET
    );

    const worldMapId = ctxEdit.map.worldMapId;

    const { getTokenForMapId } = useWorldMapLocalStorage();

    function createSet(data: Required<WorldMapSetInput>) {
        return createSetMutation({
            variables: {
                worldMapId,
                worldMapSet: data,
                token: getTokenForMapId(worldMapId)
            },
            optimisticResponse: {
                createWorldMapSet: {
                    ...data,
                    worldMapSetId: 'temporary',
                    __typename: 'WorldMapSet',
                    markers: []
                }
            },
            update: (cache, result) => {
                if (!result.data) {
                    return;
                }

                cacheAddWorldMapSet(cache, worldMapId, result.data.createWorldMapSet);
                if (data.isVisibleOnLoad) {
                    ctxEdit.setVisibleMapSetIds([
                        ...ctxEdit.visibleMapSetIds,
                        result.data.createWorldMapSet.worldMapSetId
                    ]);
                }

                ctxEdit.setMap({
                    ...ctxEdit.map,
                    worldMapSets: [...ctxEdit.map.worldMapSets, result.data.createWorldMapSet]
                });
            }
        });
    }

    function removeSet(worldMapSetId: string) {
        return removeSetMutation({
            variables: {
                worldMapSetId: worldMapSetId,
                token: getTokenForMapId(worldMapId)
            },
            update: (cache, result) => {
                if (result.data?.removeWorldMapSet?.success) {
                    cacheRemoveWorldMapSet(cache, worldMapId, worldMapSetId);
                    ctxEdit.setMap({
                        ...ctxEdit.map,
                        worldMapSets: ctxEdit.map.worldMapSets.filter(
                            (e) => e.worldMapSetId !== worldMapSetId
                        )
                    });
                }
            }
        });
    }

    function updateSets(
        setsToUpdate: {
            update: WorldMapSetInput;
            originalData: WorldMap_worldMap_worldMapSets;
        }[]
    ) {
        Promise.all(
            setsToUpdate.map((setToUpdate) => {
                const worldMapSetId = setToUpdate.originalData.worldMapSetId;
                return updateSetMutation({
                    variables: {
                        worldMapSetId,
                        worldMapSet: setToUpdate.update,
                        token: getTokenForMapId(worldMapId)
                    },
                    optimisticResponse: {
                        updateWorldMapSet: {
                            worldMapSetId,
                            ...setToUpdate.originalData,
                            ...setToUpdate.update
                        }
                    },
                    update: (cache, result) => {
                        if (result.data) {
                            cacheUpdateWorldMapSet(
                                cache,
                                worldMapId,
                                result.data.updateWorldMapSet
                            );
                        }
                    }
                });
            })
        ).then((results) => {
            const data = results.map((e) => e.data.updateWorldMapSet);

            if (data.every(Boolean)) {
                console.log('data', data);
                ctxEdit.setMap({
                    ...ctxEdit.map,
                    worldMapSets: ctxEdit.map.worldMapSets.map(
                        (e) => data.find((d) => d.worldMapSetId === e.worldMapSetId) ?? e
                    )
                });
            }
        });
    }

    function updateSet(
        worldMapSetId: string,
        data: WorldMapSetInput,
        originalData: WorldMap_worldMap_worldMapSets
    ) {
        return updateSetMutation({
            variables: {
                worldMapSetId: worldMapSetId,
                worldMapSet: data,
                token: getTokenForMapId(worldMapId)
            },
            optimisticResponse: {
                updateWorldMapSet: {
                    worldMapSetId: worldMapSetId,
                    ...originalData,
                    ...data
                }
            },
            update: (cache, result) => {
                if (result.data) {
                    cacheUpdateWorldMapSet(cache, worldMapId, result.data.updateWorldMapSet);

                    ctxEdit.setMap({
                        ...ctxEdit.map,
                        worldMapSets: ctxEdit.map.worldMapSets.map((e) =>
                            e.worldMapSetId === worldMapSetId ? result.data.updateWorldMapSet : e
                        )
                    });
                }
            }
        });
    }

    return {
        createSet,
        removeSet,
        updateSet,
        updateSets
    };
};
